import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import './Header.css';

const MUSIC_STATE_EVENT = 'dodsego:music-state';
const MUSIC_TOGGLE_EVENT = 'dodsego:music-toggle-mini-player';
const MUSIC_PLAY_EVENT = 'dodsego:music-play-mini-player';
const MUSIC_LAST_TRACK_TITLE_KEY = 'dodsego.musicplayer.lastTrackTitle.v1';
const MUSIC_LAST_PLAYING_KEY = 'dodsego.musicplayer.isPlaying.v1';

const dashboardPaths = ['/dashboard', '/dashboard/web', '/dashboard/project', '/dashboard/chat', '/dashboard/logout'];

function readLocalStorage(key, fallback = '') {
  if (typeof window === 'undefined') return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value == null ? fallback : value;
  } catch {
    return fallback;
  }
}

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 960px)').matches
      : false
  );
  const [musicTrackTitle, setMusicTrackTitle] = useState(() => readLocalStorage(MUSIC_LAST_TRACK_TITLE_KEY, 'DODSEGO'));
  const [musicIsPlaying, setMusicIsPlaying] = useState(() => readLocalStorage(MUSIC_LAST_PLAYING_KEY, '0') === '1');

  const isDashboardRoute = useMemo(
    () => dashboardPaths.some((path) => String(location.pathname || '').toLowerCase().startsWith(path)),
    [location.pathname]
  );

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const navLinkClass = useCallback(({ isActive }) => (isActive ? 'active' : undefined), []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(max-width: 960px)');
    const onChange = (event) => {
      setIsMobileViewport(event.matches);
    };
    setIsMobileViewport(query.matches);

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }

    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsMenuOpen(false);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    const onMusicState = (event) => {
      const detail = event?.detail || {};
      const nextTitle = String(detail.title || '').trim() || 'DODSEGO';
      setMusicTrackTitle(nextTitle);
      setMusicIsPlaying(Boolean(detail.playing));
    };

    window.addEventListener(MUSIC_STATE_EVENT, onMusicState);
    return () => window.removeEventListener(MUSIC_STATE_EVENT, onMusicState);
  }, []);

  const onGalleryMenuClick = useCallback((event) => {
    event.preventDefault();
    closeMenu();
    navigate('/gallery', {
      state: {
        forceOverview: true,
        forceOverviewAt: Date.now(),
      },
    });
  }, [closeMenu, navigate]);

  const onMusicPlayPauseClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    closeMenu();
    window.dispatchEvent(new CustomEvent(musicIsPlaying ? MUSIC_TOGGLE_EVENT : MUSIC_PLAY_EVENT));
  }, [closeMenu, musicIsPlaying]);

  const activeTrackTitle = String(musicTrackTitle || 'DODSEGO');
  const musicStatusText = musicIsPlaying
    ? `Pause ${activeTrackTitle}`
    : `Listen to ${activeTrackTitle}`;

  return (
    <header className={`site-header ${isMenuOpen ? 'menu-open' : ''}`}>
      <div className="site-header__bar">
        <NavLink to="/" className="site-header__logo" onClick={closeMenu} aria-label="Go to frontpage">
          <img src="/images/Fontego_Hvit_nB.png" alt="DODSEGO" className="site-header__logo-image" />
        </NavLink>

        <button
          type="button"
          className="site-header__hamburger"
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((prev) => !prev)}
        >
          {isMenuOpen ? 'X' : '☰'}
        </button>

        <nav className="site-header__nav" aria-label="Main menu">
          {!isDashboardRoute ? (
            <ul className="site-header__list">
              <li><NavLink to="/news" className={navLinkClass} onClick={closeMenu}>NEWS</NavLink></li>
              <li><NavLink to="/shows" className={navLinkClass} onClick={closeMenu}>CONCERTS</NavLink></li>
              <li className="site-header__music-item">
                <div className={`site-header__music-control ${musicIsPlaying ? 'is-playing' : ''}`}>
                  <NavLink to="/music" className={navLinkClass} onClick={closeMenu}>MUSIC</NavLink>
                  <button
                    type="button"
                    className="site-header__music-toggle"
                    aria-label={musicStatusText}
                    onClick={onMusicPlayPauseClick}
                  >
                    {musicIsPlaying ? '❚❚' : '▶'}
                  </button>
                  <span
                    className="site-header__music-status"
                    aria-live="polite"
                    role="button"
                    tabIndex={0}
                    onClick={onMusicPlayPauseClick}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        onMusicPlayPauseClick(event);
                      }
                    }}
                  >
                    {musicStatusText}
                  </span>
                </div>
              </li>
              <li><NavLink to="/merchandise" className={navLinkClass} onClick={closeMenu}>MERCHANDISE</NavLink></li>
              <li><NavLink to="/gallery" className={navLinkClass} onClick={onGalleryMenuClick}>GALLERY</NavLink></li>
              <li><NavLink to="/connect" className={navLinkClass} onClick={closeMenu}>CONNECT</NavLink></li>
            </ul>
          ) : (
            <ul className="site-header__list site-header__list--dashboard">
              <li><NavLink to="/dashboard/web" className={navLinkClass} onClick={closeMenu}>WEB</NavLink></li>
              <li><NavLink to="/dashboard/logout" className={navLinkClass} onClick={closeMenu}>LOGG UT</NavLink></li>
            </ul>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
